'use client';
import { useId as useNewsletterId } from 'react';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import type { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import React from 'react';
import { Heading } from '../ui/heading';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { RiSendPlaneFill } from 'react-icons/ri';
import { Paragraph } from '../ui/paragraph';
import Link from '@eliadmin/core/src/components/link';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(true);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async (args: Parameters<typeof newsletterSubscribe>[0]) => {
      const { newsletterSubscribe } =
        await import('@eliadmin/core/src/api/newsletter/newsletter-api');
      return newsletterSubscribe(args);
    },
    onSuccess: () => setIsSubscribed(true),
    onError: () => setIsAlreadySubscribed(true),
  });

  const locale = useLocaleStore(s => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  const GDPRhref = getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr');

  return (
    <div className="relative flex h-[500px] w-full items-end justify-end overflow-hidden rounded-[4px] p-6 md:max-w-[384px]">
      <div className="flex w-full flex-col items-start justify-start gap-4">
        <Heading title="NEWSLETTER" className={{ title: 'h5 text-white' }} tag="h2" />
        <form
          className={cn(
            'relative z-10 flex w-full flex-col items-start justify-start gap-4',
            'ea-homepage-newsletter__form',
          )}
          onSubmit={handleSubmit}
        >
          <div className={cn('flex h-12 w-full flex-row items-center justify-between gap-2')}>
            <Input
              aria-label={systemLabels('email')}
              id={`newsletter-email-${seoNewsletterId}`}
              type="email"
              name="email"
              placeholder="E-mail"
              onChange={e => setEmail(e.target.value)}
              className={{
                wrapper: cn(
                  'h-12 overflow-hidden rounded-[4px] bg-white/20 p-0',
                  'ea-homepage-newsletter__form-item-email-input',
                ),
                main: cn(
                  'h-12 bg-transparent px-[18px] text-sm font-normal text-white placeholder:font-normal placeholder:text-white/80 sm:text-base',
                ),
              }}
            />
            <Button
              type="submit"
              aria-label={t('text185')}
              size={'icon'}
              variant={'default'}
              disabled={email.length === 0 || !checked || isSubscribed}
              isLoading={subscribeMutation.isPending}
            >
              <RiSendPlaneFill size={20} className="text-white" />
            </Button>
          </div>
          <Paragraph size="sm" className="w-full text-opacity-80" color="white">
            {t('text1993')}&nbsp;
            <Link href={GDPRhref} className="underline hover:text-white/100">
              {t('text2342')}
            </Link>
            &nbsp;{t('text3389')}
          </Paragraph>
          {isSubscribed && !isAlreadySubscribed && (
            <span className={cn('font-semibold text-success')}>{t('text3139')}</span>
          )}
          {isAlreadySubscribed && (
            <span className={cn('text-error font-semibold')}>{t('text234')}</span>
          )}
        </form>
      </div>
      <div className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-newsletter__image-overlay')} />
      <Image
        src={'/images/newsletter.jpg'}
        alt="Newsletter"
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        sizes="(min-width: 768px) 384px, calc(100vw - 32px)"
      />
    </div>
  );
}
